import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Ban, FileUp, Search, UserCheck } from 'lucide-react'
import {
  api,
  ApiError,
  type OutreachCampaign,
  type OutreachImportResult,
  type OutreachProspect,
  type OutreachProspectState,
} from '@/api/client'
import { statusOf } from '@/lib/status'
import { Button, Field, FileButton, Input, Select, StatusPill, TonePill } from '@/ui'
import {
  CampaignHeader,
  ErrorNotice,
  Hollow,
  LoadingCard,
  PlainHeader,
  errorMessage,
  isAbortError,
  isValidRouteID,
} from './shared'
import { ENDING_REASON, TerminalConfirm, type EndingKind } from './TerminalConfirm'

const states: Array<OutreachProspectState | ''> = [
  '',
  'discovered',
  'qualified',
  'connection_ready',
  'connection_sent',
  'connected',
  'praise_ready',
  'praise_sent',
  'replied',
  'cta_ready',
  'cta_sent',
  'closed',
  'skipped',
  'do_not_contact',
  'invalid_profile',
]

export default function OutreachDiscovery() {
  const campaignId = Number(useParams().campaignId)
  const validID = isValidRouteID(campaignId)
  const [campaign, setCampaign] = useState<OutreachCampaign | null>(null)
  const [prospects, setProspects] = useState<OutreachProspect[]>([])
  const [state, setState] = useState<OutreachProspectState | ''>('')
  const [needsLinkedIn, setNeedsLinkedIn] = useState(false)
  const [minScore, setMinScore] = useState(0)
  const [result, setResult] = useState<OutreachImportResult | null>(null)
  const [resultCampaignID, setResultCampaignID] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'discover' | 'csv' | null>(null)
  const [loading, setLoading] = useState(validID)
  const [reloadKey, setReloadKey] = useState(0)
  const [csvChosen, setCsvChosen] = useState(false)
  const [csvKey, setCsvKey] = useState(0)
  const routeIDRef = useRef(campaignId)
  routeIDRef.current = campaignId
  // The campaign the list on screen belongs to. A reload for the same campaign
  // (after a card's action, a filter change) refreshes behind the cards: they
  // stay mounted, so what was typed or ticked on the others survives.
  const shownCampaignRef = useRef<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const sameCampaign = shownCampaignRef.current === campaignId
    if (!sameCampaign) {
      shownCampaignRef.current = null
      setCampaign(null)
      setProspects([])
      setBusy(null)
    }
    setError(null)
    if (!validID) {
      setLoading(false)
      return () => controller.abort()
    }
    if (!sameCampaign) setLoading(true)
    void Promise.all([
      api.outreachCampaign(campaignId, controller.signal),
      api.outreachProspects(campaignId, state || undefined, controller.signal),
    ])
      .then(([campaignData, prospectData]) => {
        if (controller.signal.aborted) return
        const items = prospectData.items ?? []
        if (
          campaignData.id !== campaignId ||
          items.some((prospect) => prospect.campaign_id !== campaignId)
        ) {
          throw new Error('Outreach response did not match the current campaign route')
        }
        shownCampaignRef.current = campaignId
        setCampaign(campaignData)
        setProspects(items)
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorMessage(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, reloadKey, state, validID])

  const runDiscovery = async () => {
    if (!campaign || campaign.id !== campaignId || routeIDRef.current !== campaignId) return
    const actionCampaignID = campaignId
    setBusy('discover')
    setActionError(null)
    setResult(null)
    try {
      const nextResult = await api.discoverOutreachProspects(actionCampaignID)
      if (routeIDRef.current !== actionCampaignID) return
      setResult(nextResult)
      setResultCampaignID(actionCampaignID)
      setReloadKey((value) => value + 1)
    } catch (err) {
      if (routeIDRef.current !== actionCampaignID) return
      if (
        err instanceof ApiError &&
        err.body &&
        typeof err.body === 'object' &&
        'result' in err.body
      ) {
        setResult((err.body as { result: OutreachImportResult }).result)
        setResultCampaignID(actionCampaignID)
        setReloadKey((value) => value + 1)
      }
      setActionError(errorMessage(err))
    } finally {
      if (routeIDRef.current === actionCampaignID) setBusy(null)
    }
  }

  const upload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const input = event.currentTarget.elements.namedItem('csv') as HTMLInputElement
    const file = input.files?.[0]
    if (!file || !campaign || campaign.id !== campaignId || routeIDRef.current !== campaignId)
      return
    const actionCampaignID = campaignId
    setBusy('csv')
    setActionError(null)
    setResult(null)
    try {
      const nextResult = await api.importOutreachCSV(actionCampaignID, file)
      if (routeIDRef.current !== actionCampaignID) return
      setResult(nextResult)
      setResultCampaignID(actionCampaignID)
      input.value = ''
      setCsvChosen(false)
      setCsvKey((value) => value + 1)
      setReloadKey((value) => value + 1)
    } catch (err) {
      if (routeIDRef.current !== actionCampaignID) return
      if (
        err instanceof ApiError &&
        err.body &&
        typeof err.body === 'object' &&
        'result' in err.body
      ) {
        setResult((err.body as { result: OutreachImportResult }).result)
        setResultCampaignID(actionCampaignID)
        setReloadKey((value) => value + 1)
      }
      setActionError(errorMessage(err))
    } finally {
      if (routeIDRef.current === actionCampaignID) setBusy(null)
    }
  }

  const currentCampaign = campaign?.id === campaignId ? campaign : null
  const currentProspects = currentCampaign
    ? prospects.filter((prospect) => prospect.campaign_id === campaignId)
    : []
  const visible = currentProspects.filter(
    (prospect) => (!needsLinkedIn || !prospect.linkedin_verified_at) && prospect.score >= minScore,
  )

  if (!validID) {
    return (
      <div className="space-y-4">
        <PlainHeader title="발굴" description="캠페인 주소의 ID가 올바르지 않다." />
        <ErrorNotice message="올바르지 않은 아웃리치 캠페인 ID다." />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <CampaignHeader
        campaignId={campaignId}
        title={currentCampaign ? `${currentCampaign.name} 발굴` : '아웃리치 발굴'}
        description="Product Hunt 메이커를 가져와 저장된 근거를 검토하고, LinkedIn 주소는 사람이 확인해 넣는다. 이 앱은 검색 결과나 프로필을 가져오지 않는다."
      />
      <ErrorNotice message={error} />
      <ErrorNotice message={actionError} />

      {loading && <LoadingCard>발굴 목록을 불러오는 중…</LoadingCard>}

      {!loading && !error && currentCampaign && (
        <section
          aria-label="Product Hunt 가져오기"
          className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="min-w-0">
            <h2 className="text-section font-semibold">Product Hunt 발굴</h2>
            <p className="mt-1 text-meta text-muted-foreground">
              설정된 Product Hunt API 토큰과 캠페인 조건을 쓴다. 메이커의 웹사이트나 LinkedIn은 열지 않는다.
            </p>
            <p className="mt-2 text-meta text-muted-foreground">
              CSV의 첫 줄에는 <code>product_name</code>, <code>product_hunt_url</code>, <code>upvotes</code>,{' '}
              <code>maker_name</code> 칸이 꼭 있어야 한다. <code>linkedin_url</code>, <code>maker_role</code>,{' '}
              <code>product_tagline</code>, <code>product_description</code>, <code>product_website_url</code>,{' '}
              <code>product_posted_at</code>, <code>topics</code>는 있으면 쓴다. 캠페인의 추천 수 상한과
              조회 기간에 맞지 않는 줄은 건너뛴다.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              variant="primary"
              icon={Search}
              loading={busy === 'discover'}
              disabled={currentCampaign.status !== 'active'}
              onClick={() => void runDiscovery()}
            >
              발굴 실행
            </Button>
            <form onSubmit={upload} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <FileButton
                key={csvKey}
                inputLabel="CSV 파일"
                name="csv"
                accept=".csv,text/csv"
                disabled={currentCampaign.status !== 'active' || busy !== null}
                onChange={(e) => setCsvChosen(!!e.target.files?.[0])}
              >
                CSV 고르기
              </FileButton>
              <Button
                type="submit"
                icon={FileUp}
                loading={busy === 'csv'}
                disabled={currentCampaign.status !== 'active' || !csvChosen}
              >
                CSV 가져오기
              </Button>
            </form>
          </div>
        </section>
      )}

      {result && resultCampaignID === campaignId && (
        <section aria-label="가져오기 결과" className="space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap gap-2">
            <TonePill tone="published" label={`${result.imported}건 가져옴`} />
            <TonePill tone="neutral" label={`${result.skipped}건 건너뜀`} />
            <TonePill
              tone={result.row_error_count ? 'publishing' : 'neutral'}
              label={`행 오류 ${result.row_error_count}건`}
            />
          </div>
          {(result.skipped_rows?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-table font-medium">건너뛴 줄</h3>
              <ul className="mt-1 space-y-1 text-table text-muted-foreground" aria-label="건너뛴 줄">
                {result.skipped_rows?.map((row) => (
                  <li key={`${row.row}-${row.message}`}>
                    {row.row > 0 ? `${row.row}행: ` : ''}
                    {row.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.row_errors.length > 0 && (
            <ul className="space-y-1 text-table text-status-publishing" aria-label="CSV 행 오류">
              {result.row_errors.map((rowError) => (
                <li key={`${rowError.row}-${rowError.message}`}>
                  {rowError.row}행: {rowError.message}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {!loading && !error && currentCampaign && (
        <>
          <div className="grid gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-3 sm:items-end">
            <Field label="상태">
              <Select value={state} onChange={(e) => setState(e.target.value as OutreachProspectState | '')}>
                {states.map((value) => (
                  <option key={value || 'all'} value={value}>
                    {value ? statusOf('prospect', value).label : '전체 상태'}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="최소 점수">
              <Input
                type="number"
                min={0}
                max={100}
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
              />
            </Field>
            <label className="flex h-9 items-center gap-2 text-body">
              <input
                type="checkbox"
                className="h-4 w-4 accent-accent"
                checked={needsLinkedIn}
                onChange={(e) => setNeedsLinkedIn(e.target.checked)}
              />
              <span>LinkedIn 확인 필요만</span>
            </label>
          </div>

          <div className="space-y-3">
            {visible.length === 0 && <Hollow>조건에 맞는 메이커가 없다.</Hollow>}
            {visible.map((prospect) => (
              <ProspectCard
                key={prospect.id}
                prospect={prospect}
                actionsEnabled={
                  currentCampaign.id === campaignId &&
                  prospect.campaign_id === campaignId &&
                  currentCampaign.status === 'active'
                }
                onChanged={() => setReloadKey((value) => value + 1)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function ProspectCard({
  prospect,
  actionsEnabled,
  onChanged,
}: {
  prospect: OutreachProspect
  actionsEnabled: boolean
  onChanged: () => void
}) {
  const [url, setURL] = useState(prospect.linkedin_url)
  const [confirmDuplicate, setConfirmDuplicate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ending, setEnding] = useState<EndingKind | null>(null)
  const scoreReasons = prospect.score_reason
    .split(';')
    .map((reason) => reason.trim())
    .filter(Boolean)

  const findLinkedIn = () => {
    if (!actionsEnabled) return
    const query = new URLSearchParams({ q: `${prospect.maker_name} ${prospect.product_name}` })
    window.open(`https://www.google.com/search?${query}`, '_blank', 'noopener,noreferrer')
  }

  const verify = async () => {
    if (!actionsEnabled) return
    setBusy(true)
    setError(null)
    try {
      await api.verifyOutreachLinkedIn(prospect.id, url, confirmDuplicate)
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const qualify = async () => {
    if (!actionsEnabled) return
    setBusy(true)
    setError(null)
    try {
      await api.transitionOutreachProspect(prospect.id, 'qualified', '', confirmDuplicate)
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const terminalAction = async (to: 'do_not_contact' | 'invalid_profile') => {
    setEnding(null)
    setBusy(true)
    setError(null)
    try {
      await api.transitionOutreachProspect(prospect.id, to, ENDING_REASON[to])
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const closed = ['closed', 'skipped', 'do_not_contact', 'invalid_profile'].includes(prospect.state)
  return (
    <article className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/outreach/${prospect.campaign_id}/prospects/${prospect.id}`}
              className="text-section font-semibold text-foreground hover:underline"
            >
              {prospect.maker_name}
            </Link>
            <StatusPill domain="prospect" status={prospect.state} />
            {!prospect.linkedin_verified_at && <TonePill tone="publishing" label="LinkedIn 확인 필요" />}
          </div>
          <p className="mt-1 text-table text-muted-foreground">
            {prospect.maker_role || '메이커'} · {prospect.product_name} · 추천 {prospect.product_upvotes}
          </p>
          {prospect.product_tagline && <p className="mt-2 text-body">“{prospect.product_tagline}”</p>}
        </div>
        <div className="shrink-0 rounded-md bg-muted px-3 py-2 text-center">
          <div className="text-micro text-muted-foreground">점수</div>
          <div className="text-stat font-semibold tabular-nums">{prospect.score}</div>
        </div>
      </div>
      <ul className="grid gap-1 text-meta text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
        {scoreReasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      <div className="flex flex-col gap-2 lg:flex-row">
        <Input
          aria-label={`${prospect.maker_name} LinkedIn 주소`}
          className="flex-1"
          placeholder="https://www.linkedin.com/in/..."
          value={url}
          onChange={(e) => setURL(e.target.value)}
          disabled={prospect.state !== 'discovered'}
        />
        <div className="flex flex-wrap gap-2">
          <Button icon={Search} disabled={!actionsEnabled} onClick={findLinkedIn}>
            LinkedIn 찾기
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!actionsEnabled || !url.trim() || prospect.state !== 'discovered'}
            onClick={() => void verify()}
          >
            주소 확인
          </Button>
          <Button
            variant="primary"
            icon={UserCheck}
            loading={busy}
            disabled={!actionsEnabled || !prospect.linkedin_verified_at || prospect.state !== 'discovered'}
            onClick={() => void qualify()}
          >
            적격 처리
          </Button>
          <Button loading={busy} disabled={closed} onClick={() => setEnding('invalid_profile')}>
            프로필 무효
          </Button>
          <Button variant="dangerOutline" icon={Ban} loading={busy} disabled={closed} onClick={() => setEnding('do_not_contact')}>
            연락 금지
          </Button>
        </div>
      </div>
      <label className="flex items-center gap-2 text-meta text-muted-foreground">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent"
          checked={confirmDuplicate}
          onChange={(e) => setConfirmDuplicate(e.target.checked)}
        />
        다른 진행 중 캠페인과 겹치는 것을 확인했고 받아들인다.
      </label>
      <ErrorNotice message={error} />
      <TerminalConfirm
        kind={ending}
        maker={prospect.maker_name}
        onCancel={() => setEnding(null)}
        onConfirm={(kind) => (kind === 'do_not_contact' || kind === 'invalid_profile' ? terminalAction(kind) : undefined)}
      />
    </article>
  )
}
